// src/pages/AdminPromoList.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { listPromos, deletePromo } from "../services/promoService.firebase";

/* ------- Helpers ------- */
const fmtDate = (iso) =>
  iso ? String(iso).slice(0, 16).replace("T", " ") : "—";

const nowTs = () => Date.now();
const toTs = (iso) => (iso ? new Date(iso).getTime() : null);

function PeriodBadge({ startsAt, endsAt, isEnabled }) {
  const n = nowTs();
  const s = toTs(startsAt);
  const e = toTs(endsAt);

  let label = "Toujours";
  let cls = "secondary";

  if (!isEnabled) {
    label = "Inactive";
    cls = "secondary";
  } else if (s && n < s) {
    label = "À venir";
    cls = "info";
  } else if (e && n > e) {
    label = "Expirée";
    cls = "secondary";
  } else {
    label = "En cours";
    cls = "success";
  }

  return <span className={`badge text-bg-${cls}`}>{label}</span>;
}

function StateChips({ p }) {
  return (
    <div className="d-flex flex-wrap gap-1">
      {p.isEnabled ? (
        <span className="badge text-bg-success">Active</span>
      ) : (
        <span className="badge text-bg-secondary">Inactive</span>
      )}
      {p.pinned && <span className="badge text-bg-warning text-dark">Épinglée</span>}
      <PeriodBadge startsAt={p.startsAt} endsAt={p.endsAt} isEnabled={p.isEnabled} />
    </div>
  );
}

export default function AdminPromoList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("priority_desc"); // priority_desc | start_desc | end_desc | title_asc

  const reload = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await listPromos();
      setItems(res.items || []);
    } catch (e) {
      setErr(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette promo ?")) return;
    try {
      await deletePromo(id);
      reload();
    } catch (e) {
      alert(e.message || "Suppression impossible");
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = !needle
      ? items
      : items.filter((p) => {
          const hay = `${p.title || ""} ${p.subtitle || ""} ${p.ctaLabel || ""}`.toLowerCase();
          return hay.includes(needle);
        });

    const sorted = [...base];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case "priority_desc":
          return (b.priority ?? 0) - (a.priority ?? 0);
        case "start_desc":
          return (toTs(b.startsAt) || 0) - (toTs(a.startsAt) || 0);
        case "end_desc":
          return (toTs(b.endsAt) || 0) - (toTs(a.endsAt) || 0);
        case "title_asc":
          return String(a.title || "").localeCompare(String(b.title || ""), "fr");
        default:
          return 0;
      }
    });
    return sorted;
  }, [items, q, sortBy]);

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h4 className="m-0">Promotions</h4>
          {!loading && (
            <span className="badge text-bg-secondary">
              {filtered.length} / {items.length}
            </span>
          )}
        </div>
        <Link to="/admin/promos/new" className="btn btn-primary btn-sm">
          Nouvelle promo
        </Link>
      </div>

      {/* Outils */}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-md-7">
          <label className="form-label">Recherche</label>
          <input
            className="form-control"
            placeholder="Titre, sous-titre, CTA…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-5">
          <label className="form-label">Trier par</label>
          <select
            className="form-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="priority_desc">Priorité (desc.)</option>
            <option value="start_desc">Début (récent → ancien)</option>
            <option value="end_desc">Fin (récent → ancien)</option>
            <option value="title_asc">Titre (A → Z)</option>
          </select>
        </div>
      </div>

      {/* Contenu */}
      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="alert alert-light">
          Aucune promotion.
          {q && <span className="ms-1">Essaie d’effacer la recherche.</span>}
        </div>
      ) : (
        <>
          {/* Grille cartes (mobile) */}
          <div className="d-lg-none">
            <div className="row g-3">
              {filtered.map((p) => (
                <div key={p.id} className="col-12">
                  <div className="card shadow-sm h-100">
                    <div
                      className="card-img-top"
                      style={{
                        height: 120,
                        background: `linear-gradient(135deg, ${p.gradient?.[0] || "#e3e3e3"}, ${
                          p.gradient?.[1] || "#c7c7c7"
                        })`,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {p.imageUrl && (
                        <img
                          src={p.imageUrl}
                          alt=""
                          style={{
                            position: "absolute",
                            right: 8,
                            bottom: 8,
                            height: 90,
                            width: "auto",
                            objectFit: "cover",
                            borderRadius: 8,
                            boxShadow: "0 4px 12px rgba(0,0,0,.15)",
                          }}
                        />
                      )}
                    </div>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="d-flex align-items-center gap-2">
                            <h6 className="card-title m-0">{p.title || "Sans titre"}</h6>
                            {p.priority != null && (
                              <span className="badge text-bg-dark" title="Priorité">
                                {p.priority}
                              </span>
                            )}
                          </div>
                          {p.subtitle && (
                            <div className="text-muted small mt-1">{p.subtitle}</div>
                          )}
                        </div>
                      </div>

                      <div className="mt-2">
                        <StateChips p={p} />
                      </div>

                      <div className="small text-muted mt-2">
                        {fmtDate(p.startsAt)} → {fmtDate(p.endsAt)}
                      </div>

                      <div className="d-flex gap-2 mt-3">
                        <Link
                          className="btn btn-sm btn-outline-secondary"
                          to={`/admin/promos/${encodeURIComponent(p.id)}`}
                        >
                          Éditer
                        </Link>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDelete(p.id)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tableau (desktop) */}
          <div className="d-none d-lg-block table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th style={{ width: 56 }}></th>
                  <th>Titre</th>
                  <th>Période</th>
                  <th>État</th>
                  <th>Priorité</th>
                  <th style={{ width: 160 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {/* pastille visuelle */}
                      <div
                        style={{
                          width: 40,
                          height: 28,
                          borderRadius: 6,
                          background: `linear-gradient(135deg, ${p.gradient?.[0] || "#e3e3e3"}, ${
                            p.gradient?.[1] || "#c7c7c7"
                          })`,
                          position: "relative",
                          overflow: "hidden",
                        }}
                        title="Dégradé"
                      >
                        {p.imageUrl && (
                          <img
                            src={p.imageUrl}
                            alt=""
                            style={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              height: "100%",
                              width: "auto",
                              objectFit: "cover",
                              borderTopRightRadius: 6,
                              borderBottomRightRadius: 6,
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="fw-semibold">
                      <div>{p.title || "Sans titre"}</div>
                      {p.subtitle && <div className="text-muted small">{p.subtitle}</div>}
                    </td>
                    <td className="small">
                      {fmtDate(p.startsAt)} → {fmtDate(p.endsAt)}
                    </td>
                    <td>
                      <StateChips p={p} />
                    </td>
                    <td>{p.priority ?? 0}</td>
                    <td className="text-end">
                      <Link
                        className="btn btn-sm btn-outline-secondary me-2"
                        to={`/admin/promos/${encodeURIComponent(p.id)}`}
                      >
                        Éditer
                      </Link>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => onDelete(p.id)}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}